import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../app/api";

type SearchArea = "resolutions" | "objects" | "units" | "owners";

const areaTitle = (area: SearchArea) => {
  if (area === "resolutions") return "Beschlüsse";
  if (area === "objects") return "Objekte";
  if (area === "units") return "Einheiten";
  return "Eigentümer";
};

const AreaIcon = ({ area }: { area: SearchArea }) => {
  if (area === "resolutions") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="5" y="4" width="14" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
        <path d="M8 9h8M8 13h8M8 17h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  if (area === "objects") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M3 10 12 4l9 6v10H3z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        <path d="M9 20v-6h6v6" stroke="currentColor" strokeWidth="1.8" />
      </svg>
    );
  }
  if (area === "units") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M4 4h16v16H4z" stroke="currentColor" strokeWidth="1.8" />
        <path d="M12 4v16M4 12h16" stroke="currentColor" strokeWidth="1.8" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.8" />
      <path d="M5 20a7 7 0 0 1 14 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
};

const SearchTitleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="10.5" cy="10.5" r="5.7" stroke="currentColor" strokeWidth="1.9" />
    <path d="m15 15 4.2 4.2" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
    <path d="M8.1 10.5h4.8M10.5 8.1v4.8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

export const GlobalSearchPage = () => {
  const [searchParams] = useSearchParams();
  const query = String(searchParams.get("q") ?? "").trim();

  const { data, isFetching } = useQuery({
    queryKey: ["global-search", query],
    enabled: query.length >= 2,
    queryFn: async () => {
      const safe = async <T,>(promise: Promise<T>): Promise<T | null> => {
        try {
          return await promise;
        } catch {
          return null;
        }
      };

      const [resolutionsRes, objectsRes, unitsRes, ownersRes] = await Promise.all([
        safe(api.resolutions({ page: 1, pageSize: 50, search: query })),
        safe(api.objects({ page: 1, pageSize: 50, search: query })),
        safe(api.units({ page: 1, pageSize: 50, search: query })),
        safe(api.owners({ page: 1, pageSize: 50, search: query }))
      ]);

      return {
        resolutions: resolutionsRes?.data ?? [],
        objects: objectsRes?.data ?? [],
        units: unitsRes?.data ?? [],
        owners: ownersRes?.data ?? [],
        totals: {
          resolutions: Number(resolutionsRes?.meta?.total ?? 0),
          objects: Number(objectsRes?.meta?.total ?? 0),
          units: Number(unitsRes?.meta?.total ?? 0),
          owners: Number(ownersRes?.meta?.total ?? 0)
        }
      };
    }
  });

  const sections: Array<{
    area: SearchArea;
    total: number;
    rows: Array<{ id: string; title: string; subtitle: string; href: string }>;
  }> = [
    {
      area: "resolutions",
      total: Number(data?.totals.resolutions ?? 0),
      rows: (data?.resolutions ?? []).map((row) => ({
        id: row.id,
        title: row.titel,
        subtitle: `${row.beschlussnummer} · ${row.objektnummer}`,
        href: `/resolutions/${row.id}`
      }))
    },
    {
      area: "objects",
      total: Number(data?.totals.objects ?? 0),
      rows: (data?.objects ?? []).map((row) => ({
        id: row.id,
        title: row.objektname,
        subtitle: `${row.objektnummer} · ${row.strasse} ${row.plz} ${row.ort}`.trim(),
        href: `/objects/${row.id}`
      }))
    },
    {
      area: "units",
      total: Number(data?.totals.units ?? 0),
      rows: (data?.units ?? []).map((row) => ({
        id: row.id,
        title: `${row.einheitsnummer} · ${row.bezeichnung}`,
        subtitle: `${row.objektnummer} · ${row.einheitstyp}`,
        href: `/units?search=${encodeURIComponent(row.einheitsnummer || row.bezeichnung)}`
      }))
    },
    {
      area: "owners",
      total: Number(data?.totals.owners ?? 0),
      rows: (data?.owners ?? []).map((row) => ({
        id: row.id,
        title: row.vollname || row.eigentuemer_id,
        subtitle: `${row.eigentuemer_id} · ${row.objektnummer}`,
        href: `/owners?search=${encodeURIComponent(row.eigentuemer_id || row.vollname)}`
      }))
    }
  ];

  const totalHits = sections.reduce((sum, section) => sum + section.total, 0);
  const hasSearchTerm = query.length >= 2;

  return (
    <div className="global-search-page">
      <section className="page-header panel resolutions-v2-page-header global-search-v2-header">
        <h2 className="resolutions-v2-title" style={{ margin: 0 }}>
          <span className="resolutions-v2-title-icon" aria-hidden="true">
            <SearchTitleIcon />
          </span>
          <span className="resolutions-v2-title-copy">
            <span className="resolutions-v2-title-main">Suchergebnisse</span>
            <span className="resolutions-v2-title-sub">Globale Suche über Beschlüsse, Objekte, Einheiten und Eigentümer</span>
          </span>
        </h2>
      </section>

      <section className="panel global-search-results-panel">
        <div className="global-search-summary">
          <div>
            <span className="global-search-eyebrow">Suche</span>
            <strong>{hasSearchTerm ? `"${query}"` : "Mindestens 2 Zeichen eingeben"}</strong>
          </div>
          <span className={`global-search-status${isFetching ? " is-loading" : ""}`}>
            {isFetching ? "Suche läuft..." : hasSearchTerm ? `${totalHits} Treffer gesamt` : "Bereit"}
          </span>
        </div>

        {hasSearchTerm ? (
          <div className="global-search-section-grid">
            {sections.map((section) => (
              <section key={section.area} className="global-search-section">
                <h3>
                  <span className="global-search-section-title">
                    <span className="global-search-section-icon" aria-hidden="true">
                      <AreaIcon area={section.area} />
                    </span>
                    <span>{areaTitle(section.area)}</span>
                  </span>
                  <span className="global-search-section-count">{section.total}</span>
                </h3>
                {section.rows.length === 0 ? (
                  <p className="global-search-empty">Keine Treffer in diesem Bereich.</p>
                ) : (
                  <div className="global-search-list">
                    {section.rows.map((row) => (
                      <Link key={`${section.area}-${row.id}`} to={row.href} className="global-search-row">
                        <strong>{row.title}</strong>
                        <span>{row.subtitle}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </section>
            ))}
          </div>
        ) : (
          <div className="global-search-empty-state">Bitte geben Sie oben in der Suche mindestens 2 Zeichen ein.</div>
        )}
      </section>
    </div>
  );
};
